import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type FormEvent,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';

import useApplicationsStore from '@/domains/application/applicationStore';
import {
  filterApplicationCheckpoints,
  getCheckpointsCountWithInvalidParameters,
  getLatestApplicationConfiguration,
  getPreviousApplicationConfiguration,
  getSortedApplicationConfigurations,
  mapApplicationCheckpointsToOnboardingViewModels,
  useApplicationConfigurations,
} from '@/domains/application';
import CheckpointConfig from '@/features/onboarding/components/CheckpointConfigDrawer';
import type {
  OnboardingCheckpointListProps,
  OnboardingCheckpointSearchBarProps,
  OnboardingConfigurationStatusPanelProps,
  OnboardingFlowHeaderProps,
} from '@/features/onboarding/types/onboarding.types';
import { useResponsiveViewport } from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';
import type { Checkpoint } from '@/types';

import OnboardApplicationConfigureForm from './OnboardApplicationConfigureForm';
import OnboardApplicationDetails from './OnboardApplicationDetails';
import {
  getMutationErrorMessage,
  isApplicationConfigurationNotFound,
} from './OnboardApplicationPage.utils';

type CheckpointListProps = OnboardingCheckpointListProps<Checkpoint>;

interface UseOnboardApplicationControllerResult {
  checkpointConfigProps: ComponentProps<typeof CheckpointConfig>;
  checkpointListProps: CheckpointListProps;
  checkpointSearchProps: OnboardingCheckpointSearchBarProps | null;
  configureFormProps: ComponentProps<typeof OnboardApplicationConfigureForm>;
  detailsProps: ComponentProps<typeof OnboardApplicationDetails>;
  emptyStateMessage: string | null;
  hasLatestConfiguration: boolean;
  headerProps: OnboardingFlowHeaderProps;
  isMobileViewport: boolean;
  statusProps: OnboardingConfigurationStatusPanelProps | null;
}

export const useOnboardApplicationController =
  (): UseOnboardApplicationControllerResult => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const bitId = searchParams.get('bitId') ?? '';
    const isMobileViewport = useResponsiveViewport();
    const [selectedApplication, setSelectedApplication] = useState(bitId);
    const [checkpointSearch, setCheckpointSearch] = useState('');
    const [activeCheckpoint, setActiveCheckpoint] =
      useState<Checkpoint | null>(null);

    const { showToast } = useToast(
      useShallow((store) => ({
        showToast: store.showToast,
      })),
    );

    const { applications } = useApplicationsStore(
      useShallow((store) => ({
        applications: store.applications,
      })),
    );

    const handleConfigurationMutationError = useCallback(
      (error: unknown) => {
        showToast('error', {
          message: getMutationErrorMessage(error),
        });
      },
      [showToast],
    );

    const {
      applicationConfigurations,
      applicationConfigurationsError,
      createApplicationConfiguration,
      deleteApplicationConfiguration,
      isCreatingConfiguration,
      isDeletingConfiguration,
      isFetchingApplicationConfigurations,
      isSavingConfiguration,
      refetchApplicationConfigurations,
      saveApplicationConfiguration,
    } = useApplicationConfigurations({
      bitId: selectedApplication,
      onCreateError: handleConfigurationMutationError,
      onCreateSuccess: () => {
        showToast('success', {
          message: 'Configuration created successfully',
        });
      },
      onDeleteError: handleConfigurationMutationError,
      onDeleteSuccess: () => {
        showToast('success', {
          message: 'Configuration cancelled successfully',
        });
      },
      onSaveError: handleConfigurationMutationError,
      onSaveSuccess: () => {
        showToast('success', {
          message: 'Configuration saved successfully',
        });
      },
    });

    const handleBack = useCallback(() => {
      const canGoBack = location.key !== 'default';

      if (canGoBack) {
        navigate(-1);
        return;
      }

      navigate('/applications');
    }, [location.key, navigate]);

    useEffect(() => {
      setSelectedApplication(bitId);
    }, [bitId]);

    const sortedConfigurations = useMemo(() => {
      return getSortedApplicationConfigurations(applicationConfigurations);
    }, [applicationConfigurations]);

    const latestConfiguration = useMemo(() => {
      return getLatestApplicationConfiguration(sortedConfigurations);
    }, [sortedConfigurations]);

    const previousConfiguration = useMemo(() => {
      return getPreviousApplicationConfiguration(sortedConfigurations);
    }, [sortedConfigurations]);

    const selectedApplicationOption = useMemo(() => {
      return applications.find(
        (application) => application.bitId === selectedApplication,
      );
    }, [applications, selectedApplication]);

    const checkpointsCountWithInvalidParameters = useMemo(() => {
      return getCheckpointsCountWithInvalidParameters(
        latestConfiguration,
      );
    }, [latestConfiguration]);

    const filteredCheckpoints = useMemo(() => {
      return filterApplicationCheckpoints({
        checkpointSearch,
        checkpoints: latestConfiguration?.checkpoints ?? [],
        previousConfiguration,
      });
    }, [
      checkpointSearch,
      latestConfiguration,
      previousConfiguration,
    ]);

    useEffect(() => {
      if (!applicationConfigurationsError) {
        return;
      }

      if (
        isApplicationConfigurationNotFound(
          applicationConfigurationsError,
        )
      ) {
        showToast('error', {
          autoCloseTimeout: 5000,
          message:
            'No configuration found for this application. Click on "Configure" to create a new configuration.',
        });
      }
    }, [applicationConfigurationsError, showToast]);

    useEffect(() => {
      setCheckpointSearch('');
    }, [selectedApplication, latestConfiguration?.configurationId]);

    const handleConfigureSubmit = useCallback(
      (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!latestConfiguration) {
          createApplicationConfiguration({
            bitId: selectedApplication,
          });
          return;
        }

        createApplicationConfiguration({
          bitId: selectedApplication,
          configurationId: latestConfiguration.configurationId,
        });
      },
      [
        createApplicationConfiguration,
        latestConfiguration,
        selectedApplication,
      ],
    );

    const handleSaveConfiguration = useCallback(() => {
      if (!latestConfiguration) {
        return;
      }

      saveApplicationConfiguration({
        bitId: selectedApplication,
        configurationId: latestConfiguration.configurationId,
      });
    }, [
      latestConfiguration,
      saveApplicationConfiguration,
      selectedApplication,
    ]);

    const handleCancelConfiguration = useCallback(() => {
      if (!latestConfiguration) {
        return;
      }

      deleteApplicationConfiguration({
        bitId: selectedApplication,
        configurationId: latestConfiguration.configurationId,
      });
    }, [
      deleteApplicationConfiguration,
      latestConfiguration,
      selectedApplication,
    ]);

    const handleConfigureCheckpoint = useCallback(
      (checkpoint: Checkpoint) => {
        setActiveCheckpoint(checkpoint);
      },
      [],
    );

    const handleCheckpointSearchChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setCheckpointSearch(event.target.value);
      },
      [],
    );

    const handleCloseCheckpointConfig = useCallback(() => {
      setActiveCheckpoint(null);
    }, []);

    const checkpointListProps = useMemo<CheckpointListProps>(
      () => ({
        checkpoints: mapApplicationCheckpointsToOnboardingViewModels(
          filteredCheckpoints,
          previousConfiguration,
        ),
        onConfigureCheckpoint: handleConfigureCheckpoint,
      }),
      [
        filteredCheckpoints,
        handleConfigureCheckpoint,
        previousConfiguration,
      ],
    );

    const hasLatestConfiguration = !!latestConfiguration;
    const emptyStateMessage =
      hasLatestConfiguration && filteredCheckpoints.length === 0
        ? 'No checkpoints match the current search.'
        : null;

    return {
      checkpointConfigProps: {
        bitId: selectedApplication,
        checkpoint: activeCheckpoint,
        configurationId:
          latestConfiguration?.configurationId ?? '',
        disabled: !latestConfiguration?.isDraft,
        isOpen: !!activeCheckpoint,
        onClose: handleCloseCheckpointConfig,
        refetch: refetchApplicationConfigurations,
      },
      checkpointListProps,
      checkpointSearchProps: hasLatestConfiguration
        ? {
            filteredCount: filteredCheckpoints.length,
            label: 'Search checkpoints',
            onSearchChange: handleCheckpointSearchChange,
            placeholder:
              'Search by id, description, category, accountable, or status',
            resultsLabel: 'checkpoints',
            searchValue: checkpointSearch,
            totalCount: latestConfiguration.checkpoints.length,
          }
        : null,
      configureFormProps: {
        applications,
        isCreatingConfiguration,
        isFetchingApplicationConfigurations,
        isSavingConfiguration,
        latestConfiguration,
        onSelectedApplicationChange: setSelectedApplication,
        onSubmit: handleConfigureSubmit,
        selectedApplication,
      },
      detailsProps: {
        application: selectedApplicationOption,
      },
      emptyStateMessage,
      hasLatestConfiguration,
      headerProps: {
        onBack: handleBack,
        title: 'Onboard Application',
      },
      isMobileViewport,
      statusProps: latestConfiguration
        ? {
            draftMessage:
              'Configuration is in draft mode. Please configure all checkpoints and save the configuration.',
            invalidItemCount: checkpointsCountWithInvalidParameters,
            invalidItemLabel: 'checkpoint',
            isDeleting: isDeletingConfiguration,
            isDraft: latestConfiguration.isDraft,
            isSaving: isSavingConfiguration,
            onCancel: handleCancelConfiguration,
            onSave: handleSaveConfiguration,
            publishedMessage:
              'Configuration is published and will be used for next ORR evaluation. Click on Reconfigure to make changes.',
            totalConfigurations:
              applicationConfigurations?.length ?? 0,
          }
        : null,
    };
  };
